import React, { useState, useEffect } from 'react';
import { adminAPI } from '../../services/api';
import { TableSkeleton } from '../../components/skeletons';
import { useAuth } from '../../context/AuthContext';

const GREEN = '#1B5C3A';
const GOLD = '#F5A623';
const PURPLE = '#4C1D95';
const CREAM = '#FAF9F6';
const CHAR = '#1a1a1a';
const MUTED = '#6b7280';
const BORDER = '#e5e7eb';

const statusBadge = (status) => {
  const map = {
    open: { bg: '#dcfce7', color: GREEN, label: 'Open' },
    active: { bg: '#dcfce7', color: GREEN, label: 'Active' },
    closed: { bg: '#f3f4f6', color: MUTED, label: 'Closed' },
    flagged: { bg: '#fee2e2', color: '#dc2626', label: 'Flagged' },
    expired: { bg: '#fef3c7', color: '#d97706', label: 'Expired' },
  };
  const s = map[status] || { bg: '#f3f4f6', color: MUTED, label: status || 'Unknown' };
  return (
    <span style={{
      display: 'inline-block', padding: '3px 10px', borderRadius: 20,
      fontSize: 11, fontWeight: 700, background: s.bg, color: s.color,
    }}>
      {s.label}
    </span>
  );
};

export default function DemandModeration() {
  useAuth();
  const [demands, setDemands] = useState([]);
  const [loading, setLoading] = useState(true);
  const [acting, setActing] = useState({});
  const [filter, setFilter] = useState('all');

  const loadDemands = () => {
    adminAPI.demands(filter !== 'all' ? { status: filter } : {})
      .then(r => setDemands(r.data.data || r.data || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  };

  useEffect(() => { loadDemands(); }, [filter]);

  const moderate = async (id, action) => {
    setActing(prev => ({ ...prev, [id]: action }));
    try {
      const status = action === 'flag' ? 'flagged' : 'closed';
      await adminAPI.moderateDemand(id, { status });
      loadDemands();
    } catch (e) {
      alert(e.response?.data?.message || 'Action failed');
    }
    setActing(prev => ({ ...prev, [id]: null }));
  };

  if (loading) return <TableSkeleton rows={6} columns={5} style={{ padding: 24 }} />;

  return (
    <div style={{ padding: 24, background: CREAM, minHeight: '100vh' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
        <div>
          <h1 style={{ fontFamily: 'var(--font-serif)', fontSize: 26, fontWeight: 700, color: CHAR, margin: 0 }}>Demand Moderation</h1>
          <p style={{ color: MUTED, fontSize: 13, marginTop: 4 }}>Review and moderate buyer demands</p>
        </div>
        <div style={{ display: 'flex', gap: 6 }}>
          {['all', 'open', 'active', 'flagged', 'closed'].map(f => (
            <button
              key={f}
              onClick={() => { setFilter(f); setLoading(true); }}
              style={{
                padding: '7px 16px', borderRadius: 20, fontSize: 12, fontWeight: 600,
                border: `1px solid ${filter === f ? PURPLE : BORDER}`,
                background: filter === f ? PURPLE : '#fff',
                color: filter === f ? '#fff' : MUTED,
                cursor: 'pointer',
              }}
            >
              {f.charAt(0).toUpperCase() + f.slice(1)}
            </button>
          ))}
        </div>
      </div>

      <div className="card" style={{ overflow: 'hidden' }}>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ background: '#f9fafb' }}>
                {['ID', 'Title', 'Description', 'Buyer', 'Category', 'Budget', 'Offers', 'Status', 'Actions'].map(h => (
                  <th key={h} style={{ padding: '12px 14px', textAlign: 'left', fontSize: 11, fontWeight: 700, color: MUTED, textTransform: 'uppercase', letterSpacing: 0.5, borderBottom: `2px solid ${BORDER}` }}>
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {demands.length === 0 ? (
                <tr><td colSpan={9} style={{ padding: 40, textAlign: 'center', color: MUTED, fontSize: 13 }}>No demands found</td></tr>
              ) : (
                demands.map(d => (
                  <tr key={d._id} style={{ borderBottom: `1px solid ${BORDER}` }}>
                    <td style={{ padding: '12px 14px', fontSize: 12, color: MUTED, fontFamily: 'monospace' }}>
                      {d._id?.slice(-6).toUpperCase()}
                    </td>
                    <td style={{ padding: '12px 14px', fontSize: 13, fontWeight: 600, color: CHAR, maxWidth: 200 }}>
                      <div style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{d.title}</div>
                    </td>
                    <td style={{ padding: '12px 14px', fontSize: 12, color: MUTED, maxWidth: 220 }}>
                      <div style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{d.description || '-'}</div>
                    </td>
                    <td style={{ padding: '12px 14px' }}>
                      <div style={{ fontSize: 13, color: CHAR }}>{d.buyerId?.fullName || d.buyerName || '-'}</div>
                    </td>
                    <td style={{ padding: '12px 14px', fontSize: 13, color: CHAR }}>
                      {d.categoryId?.name || d.categoryName || '-'}
                    </td>
                    <td style={{ padding: '12px 14px', fontSize: 13, fontWeight: 600, color: GREEN }}>
                      {d.price ? `₹${d.price.toLocaleString('en-IN')}` : '-'}
                    </td>
                    <td style={{ padding: '12px 14px' }}>
                      <span style={{
                        display: 'inline-block', padding: '2px 10px', borderRadius: 20,
                        fontSize: 12, fontWeight: 700, background: PURPLE + '15', color: PURPLE,
                      }}>
                        {d.offersCount ?? 0}
                      </span>
                    </td>
                    <td style={{ padding: '12px 14px' }}>{statusBadge(d.status)}</td>
                    <td style={{ padding: '12px 14px' }}>
                      <div style={{ display: 'flex', gap: 6 }}>
                        {d.status !== 'flagged' && (
                          <button
                            onClick={() => moderate(d._id, 'flag')}
                            disabled={!!acting[d._id]}
                            style={{
                              background: '#fef3c7', color: '#d97706', border: 'none', borderRadius: 6,
                              padding: '6px 12px', fontSize: 12, fontWeight: 600, cursor: 'pointer',
                              opacity: acting[d._id] === 'flag' ? 0.6 : 1,
                            }}
                          >
                            Flag
                          </button>
                        )}
                        {d.status !== 'closed' && (
                          <button
                            onClick={() => moderate(d._id, 'close')}
                            disabled={!!acting[d._id]}
                            style={{
                              background: '#fee2e2', color: '#dc2626', border: 'none', borderRadius: 6,
                              padding: '6px 12px', fontSize: 12, fontWeight: 600, cursor: 'pointer',
                              opacity: acting[d._id] === 'close' ? 0.6 : 1,
                            }}
                          >
                            Close
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
